
<p-toast />
<p-confirmDialog />

<!-- Job Launch Dialog -->
<p-dialog
  [(visible)]="jobLaunchDialogVisible"
  [header]="'Launch Job: ' + (selectedJobConfigDto?.name || 'Select a job')"
  [modal]="true"
  [style]="{ width: '700px' }"
  [closable]="true"
  appendTo="body"
  (onHide)="jobLaunchDialogVisible = false">
  @if(selectedJobConfigDto) {
    <div class="flex flex-column gap-4">
      <!-- Job Description -->
      <div class="surface-ground p-3 border-round">
        <h5 class="m-0 mb-2">{{ selectedJobConfigDto.name }}</h5>
        <p class="text-sm text-color-secondary m-0">
          @if(isReindexJob(selectedJobConfigDto.name)) {
            <span>Reindex/Backfill job - No file input required</span>
          } @else {
            <span>File-based import job</span>
          }
        </p>
        @if(selectedJobConfigDto.jobParameters && getJobParameters().length > 0) {
          <div class="mt-3">
            <small class="text-color-secondary">Default Parameters:</small>
            <div class="flex flex-wrap gap-2 mt-1">
              @for(param of getJobParameters(); track param.key) {
                <p-tag [value]="param.key + '=' + param.value" severity="info" [rounded]="true" />
              }
            </div>
          </div>
        }
      </div>

      <!-- File Selection (for file-based jobs) -->
      @if(!isReindexJob(selectedJobConfigDto.name)) {
        <p-fieldset legend="File Input" [toggleable]="true" [collapsed]="false">
          <div class="flex flex-column gap-3">
            @if(availableFiles.length > 0) {
              <div>
                <label for="fileSelect" class="block mb-2 font-semibold">Select Input File</label>
                <p-select
                  id="fileSelect"
                  [options]="availableFiles"
                  [(ngModel)]="selectedFile"
                  placeholder="Select a file..."
                  [filter]="true"
                  [showClear]="true"
                  class="w-full" />
              </div>
            } @else if(availableFiles.length === 0) {
              <div class="surface-ground p-3 border-round border-yellow-300">
                <i class="pi pi-exclamation-triangle text-yellow-500 mr-2"></i>
                <span class="text-sm">No input files available for this job</span>
              </div>
            }
            
            <!-- Max Rows -->
            <div>
              <label for="maxRows" class="block mb-2 font-semibold">
                Max Rows to Process
                <small class="text-color-secondary ml-2">(-1 for all rows)</small>
              </label>
              <p-inputNumber
                id="maxRows"
                [(ngModel)]="maxRows"
                [min]="-1"
                [max]="999999999"
                [step]="1"
                class="w-full" />
            </div>
          </div>
        </p-fieldset>
      }

      <!-- Custom Parameters Section -->
      @if(selectedJobConfigDto.jobParameters && getJobParameters().length > 0) {
        <p-fieldset legend="Custom Parameters" [toggleable]="true" [collapsed]="false">
          <div class="flex flex-column gap-3">
            @for(param of getJobParameters(); track param.key) {
              <div class="flex flex-column gap-1">
                <label class="font-semibold">
                  {{ getParameterDescription(param.key) }}
                  @if(isParameterRequired(param.key)) {
                    <span class="text-red-500 ml-1">*</span>
                  }
                </label>
                @switch(getParameterType(param.key)) {
                  @case('string') {
                    <p-inputText 
                      [(ngModel)]="customParameters[param.key]" 
                      attr.placeholder="{{ getParameterDefault(param.key) || '' }}" 
                      class="w-full" />
                  }
                  @case('number') {
                    <p-inputNumber 
                      [(ngModel)]="customParameters[param.key]" 
                      attr.placeholder="{{ getParameterDefault(param.key) || '' }}" 
                      class="w-full" />
                  }
                  @case('boolean') {
                    <p-checkbox 
                      [(ngModel)]="customParameters[param.key]" 
                      [binary]="true" />
                  }
                  @default {
                    <p-inputText 
                      [(ngModel)]="customParameters[param.key]" 
                      attr.placeholder="{{ getParameterDefault(param.key) || '' }}" 
                      class="w-full" />
                  }
                }
              </div>
            }
          </div>
        </p-fieldset>
      }

      <!-- Dialog Actions -->
      <div class="flex justify-content-end gap-2 mt-2">
        <p-button
          label="Cancel"
          icon="pi pi-times"
          (onClick)="jobLaunchDialogVisible = false"
          [outlined]="true"
          [severity]="'secondary'" />
        <p-button
          label="Start Job"
          icon="pi pi-play"
          (onClick)="startJob()"
          [loading]="isStartingJob"
          [disabled]="!isStartJobEnabled()"
          [severity]="'success'" />
      </div>
    </div>
  } @else {
    <p class="text-color-secondary">No job configuration selected</p>
  }
</p-dialog>

<!-- Rollback Report Dialog -->
<p-dialog
  [(visible)]="rollbackReportVisible"
  [header]="'Rollback Report — Execution #' + activeRollbackExecutionId"
  [modal]="true"
  [style]="{ width: '600px' }"
  [closable]="true"
  appendTo="body">
  @if(activeRollbackReport) {
    <div class="flex flex-column gap-3">
      <div class="flex gap-4">
        <div class="surface-ground p-3 border-round flex-1 text-center">
          <div class="text-2xl font-bold text-green-600">{{ totalDeletedCount(activeRollbackReport) }}</div>
          <small class="text-color-secondary">Documents Deleted</small>
        </div>
        <div class="surface-ground p-3 border-round flex-1 text-center">
          <div class="text-2xl font-bold text-orange-500">{{ totalSkippedCount(activeRollbackReport) }}</div>
          <small class="text-color-secondary">Documents Skipped</small>
        </div>
      </div>

      @if(collectionEntries(activeRollbackReport.deletedByCollection).length > 0) {
        <div>
          <h6 class="mb-2">Deleted by Collection</h6>
          <table class="w-full text-sm">
            @for(entry of collectionEntries(activeRollbackReport.deletedByCollection); track entry.key) {
              <tr>
                <td class="font-mono py-1 pr-3">{{ entry.key }}</td>
                <td class="text-right font-bold text-green-600">{{ entry.value }}</td>
              </tr>
            }
          </table>
        </div>
      }

      @if(collectionEntries(activeRollbackReport.skippedByCollection).length > 0) {
        <div>
          <h6 class="mb-2">Skipped (still referenced)</h6>
          @for(entry of collectionEntries(activeRollbackReport.skippedByCollection); track entry.key) {
            <div class="mb-1">
              <span class="font-mono text-sm font-bold">{{ entry.key }}</span>
              <span class="ml-2 text-color-secondary text-sm">({{ entry.value.length }} items)</span>
            </div>
          }
        </div>
      }

      @if(activeRollbackReport.warnings && activeRollbackReport.warnings.length > 0) {
        <div class="surface-ground p-3 border-round border-left-3 border-orange-400">
          <h6 class="m-0 mb-2 text-orange-600"><i class="pi pi-exclamation-triangle mr-2"></i>Warnings</h6>
          @for(w of activeRollbackReport.warnings; track $index) {
            <p class="m-0 text-sm">{{ w }}</p>
          }
        </div>
      }

      <div class="text-color-secondary text-xs">
        Started: {{ activeRollbackReport.startedAt }}<br>
        Completed: {{ activeRollbackReport.completedAt }}
      </div>
    </div>
  } @else {
    <span class="text-color-secondary">No report data available.</span>
  }
</p-dialog>

<div class="flex flex-column p-1 m-1">
  <div class="flex flex-column gap-3">
    <div class="flex align-items-center gap-3">
      <h1 class="m-0">Available Batch Job Configurations</h1>
      <p-button
        label="Start Job"
        icon="pi pi-play"
        (onClick)="openJobLaunchDialog()"
        [disabled]="!selectedJobConfigDto"
        [severity]="'success'"
        tooltip="Launch the selected job with customizable parameters"
        tooltipPosition="top" />
    </div>
    <p-select [options]="jobConfigDtos" [(ngModel)]="selectedJobConfigDto" optionLabel="name" placeholder="Please select a batch job config" class="w-full"></p-select>
  </div>
  <div>
    <div class="flex align-items-center gap-3">
      <h1 class="m-0">Batch Jobs with at least one excecution</h1>
      <p-button
        icon="pi pi-refresh"
        (onClick)="refreshJobInstances()"
        tooltip="Refresh job list"
        tooltipPosition="top"
        [rounded]="true"
        [text]="true" />
    </div>
    <p-select [options]="jobDtos" [(ngModel)]="selectedJobDto" optionLabel="name" placeholder="Please select a batch job" class="w-full"></p-select>
  </div>

  <div>
    <h1>Batch Job Instances</h1>
    <div style="overflow-x: auto; min-width: 0;">
    <p-table
      [value]="selectedJobDto?.jobInstances"
      selectionMode="single"
      [(selection)]="selectedJobInstanceDto"
      dataKey="instanceId"
      stripedRows
      [expandedRowKeys]="expandedRows"
      (onRowExpand)="onRowExpand($event)">
      <ng-template #header>
        <tr>
            <th style="width: 5rem"></th>
            <th>Job Name</th>
            <th>Status</th>
            <th>Job Id</th>
            <th>Job Instance Id</th>
            <th>Job Version</th>
            <th>Executions</th>
        </tr>
      </ng-template>
      <ng-template #body let-jobInstanceDto let-expanded="expanded">
          <tr [pSelectableRow]="jobInstanceDto">
            <td>
              <p-button type="button" pRipple [pRowToggler]="jobInstanceDto" [text]="true" [rounded]="true" [plain]="true" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
            </td>
            <td>
                <div class="font-bold">{{ jobInstanceDto?.name }}</div>
                <small class="text-color-secondary">ID: {{ jobInstanceDto?.id }}</small>
            </td>
            <td></td>
            <td class="font-mono text-sm">{{ jobInstanceDto?.id }}</td>
            <td class="font-mono text-sm">{{ jobInstanceDto?.instanceId }}</td>
            <td><span class="badge bg-primary">{{ jobInstanceDto?.version }}</span></td>
            <td class="text-center">
                <span class="badge bg-secondary">{{ jobInstanceDto?.jobExecutions?.length || 0 }}</span>
            </td>
          </tr>
      </ng-template>
      <ng-template #expandedrow let-jobInstanceDto>
        <tr>
            <td colspan="7">
                <div class="p-2">
                    <!-- Job Instance Summary Card -->
                    <div class="surface-card p-4 border-1 surface-border border-round mb-4">
                        <div class="flex justify-content-between align-items-start flex-wrap gap-3">
                            <div>
                                <h5 class="m-0">{{ jobInstanceDto?.name }}</h5>
                                <small class="text-color-secondary">Job Instance ID: {{ jobInstanceDto?.instanceId }}</small>
                            </div>
                            <div class="flex gap-2">
                                <div class="flex flex-column align-items-center">
                                    <span class="font-bold text-lg">{{ jobInstanceDto?.id }}</span>
                                    <small class="text-color-secondary">Job ID</small>
                                </div>
                                <div class="flex flex-column align-items-center">
                                    <span class="font-bold text-lg">{{ jobInstanceDto?.version }}</span>
                                    <small class="text-color-secondary">Version</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Job Executions Table -->
                    <h5 class="mb-3">Job Executions ({{ jobInstanceDto?.jobExecutions?.length || 0 }})</h5>
                    <div style="overflow-x: auto; min-width: 0;">
                    <p-table [style]="{ width: '100%' }" [value]="jobInstanceDto?.jobExecutions" responsiveLayout="stack">
                      <ng-template #header>
                        <tr>
                            <th>Status</th>
                            <th>Execution ID</th>
                            <th>Job Name</th>
                            <th>Submission</th>
                            <th>Actions</th>
                        </tr>
                      </ng-template>
                      <ng-template #body let-jobExecutionDto>
                          <tr>
                              <td>
                                  <span class="p-column-title">Status</span>
                                  @if(jobExecutionDto?.status === "FAILED") {
                                    <p-tag [value]="jobExecutionDto?.status" severity="danger" icon="pi pi-times" />
                                  } @else if(jobExecutionDto?.status === "COMPLETED") {
                                    <p-tag [value]="jobExecutionDto?.status" severity="success" icon="pi pi-check" />
                                  } @else if(jobExecutionDto?.status === "RUNNING") {
                                    <p-tag [value]="jobExecutionDto?.status" severity="warn" icon="pi pi-spin pi-spinner" />
                                  } @else {
                                    <p-tag [value]="jobExecutionDto?.status" severity="info" />
                                  }
                                  @if(wasRolledBack(jobExecutionDto?.executionId)) {
                                    <p-tag value="ROLLED BACK" severity="warn" icon="pi pi-undo" class="ml-1" />
                                  }
                              </td>
                              <td>
                                  <span class="p-column-title">Execution ID</span>
                                  <small class="text-color-secondary">ID:</small><br>
                                  <span class="font-mono text-sm">{{ jobExecutionDto?.executionId }}</span>
                              </td>
                              <td>
                                  <span class="p-column-title">Job Name</span>
                                  <small class="text-color-secondary">{{ jobExecutionDto?.jobName }}</small>
                              </td>
                              <td>
                                  <span class="p-column-title">Submission</span>
                                  @if(jobExecutionDto?.jobParameters?.submissionId?.value) {
                                    <a [routerLink]="['/submit/study', jobExecutionDto.jobParameters.submissionId.value]" class="p-link">
                                      <i class="pi pi-file-edit mr-2"></i>Edit {{ jobExecutionDto.jobParameters.submissionId.value }}
                                    </a>
                                  } @else {
                                    <span class="text-color-secondary italic">No submission</span>
                                  }
                              </td>
                              <td>
                                  <div class="flex gap-1 align-items-center">
                                      <p-button
                                        (onClick)="loadJobLogs(jobInstanceDto, jobExecutionDto?.executionId, jobExecutionDto?.jobId)"
                                        [text]="true" [rounded]="true" [plain]="true"
                                        icon="pi pi-list"
                                        tooltip="View Logs" tooltipPosition="top" />
                                      @if(wasRolledBack(jobExecutionDto?.executionId)) {
                                        <p-button
                                          (onClick)="showRollbackReport(jobExecutionDto?.executionId)"
                                          [text]="true" [rounded]="true"
                                          icon="pi pi-eye"
                                          severity="warn"
                                          tooltip="View Rollback Report" tooltipPosition="top" />
                                      }
                                      @if(isRollbackEligible(jobExecutionDto) && !wasRolledBack(jobExecutionDto?.executionId)) {
                                        <p-button
                                          (onClick)="confirmRollback(jobExecutionDto)"
                                          [text]="true" [rounded]="true"
                                          icon="pi pi-undo"
                                          severity="danger"
                                          tooltip="Trigger Rollback" tooltipPosition="top" />
                                      }
                                  </div>
                              </td>
                          </tr>
                      </ng-template>
                    </p-table>
                    </div>

                    <!-- Execution Detail Cards -->
                    @if(jobInstanceDto?.jobExecutions?.length > 0) {
                    <div class="mt-4">
                        <h6 class="mb-3">Execution Details</h6>
                        @for (execution of jobInstanceDto.jobExecutions; track execution.executionId) {
                        <div class="surface-card p-4 border-1 surface-border border-round mb-3">
                            <div class="flex justify-content-between align-items-center mb-3">
                                <h6 class="m-0">
                                    <span class="mr-2">Execution ID:</span>
                                    <code class="text-sm">{{ execution.executionId }}</code>
                                    @if(wasRolledBack(execution.executionId)) {
                                      <p-tag value="ROLLED BACK" severity="warn" icon="pi pi-undo" class="ml-2" />
                                    }
                                </h6>
                                <div class="flex gap-2">
                                    <p-button
                                      (onClick)="loadJobLogs(jobInstanceDto, execution.executionId, execution.jobId)"
                                      [label]="'View Logs'" icon="pi pi-list" [text]="true" [rounded]="true" />
                                    @if(execution.jobParameters?.submissionId?.value) {
                                      <a [routerLink]="['/submit/study', execution.jobParameters.submissionId.value]"
                                         pButton label="Edit Submission" icon="pi pi-file-edit"
                                         class="p-button-outlined p-button-sm"></a>
                                    }
                                    @if(wasRolledBack(execution.executionId)) {
                                      <p-button
                                        (onClick)="showRollbackReport(execution.executionId)"
                                        label="Rollback Report" icon="pi pi-eye"
                                        severity="warn" [text]="true" [rounded]="true" />
                                    }
                                    @if(isRollbackEligible(execution) && !wasRolledBack(execution.executionId)) {
                                      <p-button
                                        (onClick)="confirmRollback(execution)"
                                        label="Trigger Rollback" icon="pi pi-undo"
                                        severity="danger" [outlined]="true" [rounded]="true" />
                                    }
                                </div>
                            </div>

                            <!-- Rollback Info Banner -->
                            @if(wasRolledBack(execution.executionId)) {
                              @let status = rollbackStatuses.get(execution.executionId);
                              <div class="surface-ground border-1 border-orange-300 border-round p-3 mb-3 flex align-items-start gap-2">
                                <i class="pi pi-undo text-orange-500 mt-1"></i>
                                <div>
                                  <span class="font-semibold text-orange-700">This import was rolled back.</span>
                                  @if(status?.rollbackSummary) {
                                    <p class="m-0 text-sm text-color-secondary mt-1">{{ status?.rollbackSummary }}</p>
                                  }
                                </div>
                              </div>
                            }

                            <!-- Timing Grid -->
                            <div class="flex flex-row flex-wrap gap-2 mb-3">
                                <div class="w-full md:w-6 lg:w-3">
                                    <div class="surface-ground p-3 border-1 surface-border border-round">
                                        <small class="text-color-secondary block mb-1">Created</small>
                                        <span class="text-sm">{{ execution.createTime || 'N/A' }}</span>
                                    </div>
                                </div>
                                <div class="w-full md:w-6 lg:w-3">
                                    <div class="surface-ground p-3 border-1 surface-border border-round">
                                        <small class="text-color-secondary block mb-1">Started</small>
                                        <span class="text-sm">{{ execution.startTime || 'N/A' }}</span>
                                    </div>
                                </div>
                                <div class="w-full md:w-6 lg:w-3">
                                    <div class="surface-ground p-3 border-1 surface-border border-round">
                                        <small class="text-color-secondary block mb-1">Ended</small>
                                        <span class="text-sm">{{ execution.endTime || 'N/A' }}</span>
                                    </div>
                                </div>
                                <div class="w-full md:w-6 lg:w-3">
                                    <div class="surface-ground p-3 border-1 surface-border border-round">
                                        <small class="text-color-secondary block mb-1">Last Updated</small>
                                        <span class="text-sm">{{ execution.lastUpdateTime || 'N/A' }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Job Parameters -->
                            <div class="border-top-1 surface-border pt-3">
                                <p-accordion [value]="0">
                                    <p-accordion-panel value="0">
                                        <p-accordion-header>
                                            <i class="pi pi-info-circle mr-2"></i>Job Parameters
                                        </p-accordion-header>
                                        <p-accordion-content>
                                            <div class="overflow-auto" style="max-height: 200px;">
                                                <pre class="text-sm m-0 p-2 surface-ground border-1 surface-border border-round">{{ execution.jobParameters | json }}</pre>
                                            </div>
                                        </p-accordion-content>
                                    </p-accordion-panel>
                                </p-accordion>
                            </div>

                            <!-- Exceptions -->
                            @if(execution.failureExceptions && execution.failureExceptions.length > 0) {
                            <div class="border-top-1 surface-border pt-3 mt-3">
                                <p-accordion [value]="0">
                                    <p-accordion-panel value="0">
                                        <p-accordion-header>
                                            <i class="pi pi-exclamation-triangle mr-2 text-red-500"></i>Exceptions ({{ execution.failureExceptions?.length }})
                                        </p-accordion-header>
                                        <p-accordion-content>
                                            <div class="overflow-auto" style="max-height: 200px;">
                                                <pre class="text-sm m-0 p-2 surface-ground border-1 surface-border border-round bg-red-50">{{ execution.failureExceptions | json }}</pre>
                                            </div>
                                        </p-accordion-content>
                                    </p-accordion-panel>
                                </p-accordion>
                            </div>
                            }
                        </div>
                        } @empty {
                        <p class="text-color-secondary italic">No job executions found for this instance.</p>
                        }
                    </div>
                    }

                    <!-- Log viewing section -->
                    @if(selectedJobExecutionForLogs && jobLogs) {
                    <div class="mt-4 p-3 border-1 surface-border border-round">
                        <div class="flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 class="m-0">Logs for "{{ selectedJobExecutionForLogs.jobName }}"</h6>
                                <small class="text-color-secondary">Execution ID: {{ selectedJobExecutionForLogs.executionId }}</small>
                            </div>
                            <div class="flex gap-2">
                                <p-button
                                  (onClick)="refreshJobLogs()"
                                  icon="pi pi-refresh" [rounded]="true" [plain]="true"
                                  tooltip="Refresh Logs" tooltipPosition="top" />
                                <p-button
                                  (onClick)="selectedJobExecutionForLogs = undefined; jobLogs = undefined"
                                  icon="pi pi-times" [rounded]="true" [plain]="true" [text]="true" />
                            </div>
                        </div>

                        <div class="flex justify-content-between align-items-center mb-2">
                            <span class="text-sm text-color-secondary">Showing {{ jobLogs.page?.size || 0 }} logs per page</span>
                            <div class="flex align-items-center gap-2">
                                <span class="text-sm">Page {{ jobLogs.page?.number + 1 }} of {{ jobLogs.page?.totalPages }}</span>
                                <p-paginator
                                    [rows]="logPageSize"
                                    [first]="logCurrentPage * logPageSize"
                                    [totalRecords]="jobLogs.page?.totalElements || 0"
                                    (onPageChange)="onLogPageChange($event)"
                                    [showCurrentPageReport]="true"
                                    [rowsPerPageOptions]="[10, 20, 50, 100]" />
                            </div>
                        </div>

                        <div class="overflow-auto" style="max-height: 400px;">
                            <table class="p-datatable-table">
                                <thead class="p-datatable-thead">
                                    <tr>
                                        <th class="p-text-left">Timestamp</th>
                                        <th class="p-text-left">Level</th>
                                        <th class="p-text-left">Message</th>
                                    </tr>
                                </thead>
                                <tbody class="p-datatable-tbody">
                                    @for (log of jobLogs._embedded?.logMessageDtoList; track $index) {
                                    <tr>
                                        <td class="p-text-left">{{ log?.dateCreated || 'N/A' }}</td>
                                        <td class="p-text-left">
                                            @if(log?.level === "ERROR") {
                                                <p-tag value="ERROR" severity="danger" icon="pi pi-times-circle" />
                                            } @else if(log?.level === "WARN") {
                                                <p-tag value="WARN" severity="warn" icon="pi pi-exclamation-triangle" />
                                            } @else if(log?.level === "INFO") {
                                                <p-tag value="INFO" severity="success" icon="pi pi-info-circle" />
                                            } @else if(log?.level === "DEBUG") {
                                                <p-tag value="DEBUG" severity="secondary" />
                                            } @else {
                                                <p-tag value="{{ log?.level || 'UNKNOWN' }}" severity="info" />
                                            }
                                        </td>
                                        <td class="p-text-left font-mono text-sm">{{ log?.message || 'No message content' }}</td>
                                    </tr>
                                    } @empty {
                                    <tr>
                                        <td colspan="3" class="p-text-center text-color-secondary">No logs available</td>
                                    </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                    }
                </div>
            </td>
        </tr>
    </ng-template>
    </p-table>
    </div>
  </div>
</div>
